/**
 * Browser-side `list_timepoints`: read-only. The loaded patient's scans in
 * date order, each with its structure sets and their TARGET structures
 * (GTV / CTV / PTV), and how the scan is aligned to the scan shown now.
 * Also the date helpers the comparison ops share. Ports injected.
 */
import type { ListTimepointsOutput } from '../../contract';

export const TARGET_TYPES = ['GTV', 'CTV', 'PTV'] as const;
export type TargetType = (typeof TARGET_TYPES)[number];

export const isTargetType = (t: string): t is TargetType =>
    (TARGET_TYPES as ReadonlyArray<string>).includes(t);

type Ok = Extract<ListTimepointsOutput, { status: 'ok' }>;
export type Timepoint = Ok['timepoints'][number];
export type TimepointSet = Timepoint['structureSets'][number];
export type RegistrationState = Timepoint['registration'];

export interface TimepointDeps {
    /** The patient's scans; undefined when no patient is loaded. */
    timepoints(): ReadonlyArray<Timepoint> | undefined;
}

/** DICOM DA(+TM) "YYYYMMDD…" → "YYYY-MM-DD"; "" when absent/invalid. */
export function dicomDateToIso(value: string | undefined): string {
    const m = /^(\d{4})(\d{2})(\d{2})/.exec(value ?? '');
    if (!m) {
        return '';
    }
    const iso = `${m[1]}-${m[2]}-${m[3]}`;
    return Number.isNaN(Date.parse(`${iso}T00:00:00Z`)) ? '' : iso;
}

/** Whole days from `a` to `b` (YYYY-MM-DD); undefined when either is unknown. */
export function daysBetween(a: string, b: string): number | undefined {
    if (!a || !b) {
        return undefined;
    }
    const ms = Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`);
    return Number.isFinite(ms) ? Math.round(ms / 86_400_000) : undefined;
}

export function listTimepoints(deps: TimepointDeps): ListTimepointsOutput {
    const tps = deps.timepoints();
    if (!tps) {
        return { status: 'unavailable', reason: 'no patient is loaded' };
    }
    // Date order, oldest first; unknown dates last.
    const sorted = [...tps].sort((a, b) =>
        a.date && b.date
            ? a.date.localeCompare(b.date)
            : a.date
              ? -1
              : b.date
                ? 1
                : 0
    );
    return { status: 'ok', timepoints: sorted };
}
